/**
 * Water-temperature extraction for the open-water swim header — DISPLAY ONLY.
 *
 * The founding fixture (Scott's 2026-07-10 Bowles Lake swim) carries NEITHER a
 * waterTemperature bucket NOR a workout.metadata dump — our exporter didn't read
 * them yet (V2 native round). So the ONLY path this app can render TODAY is the
 * omit path: no temp signal → return null → the header shows the "Open water"
 * marker with no temperature. The present-path (a temp actually exists) is a V2
 * shape, tested against a hand-extended fixture until the exporter ships it.
 *
 * NEVER fabricate a temperature. Precedence per spec §1/§4-V2: the
 * waterTemperature bucket average first, then a workout.metadata average-water-
 * temp key, then omit.
 */
import type { SwimFirehoseResponse, FirehoseTypeBucket } from "@contract/types";
import { readMetaValue } from "./healthKitLabels";

/** HealthKit water-temperature quantity type (V2 exporter addition). */
const WATER_TEMP_TYPE = "HKQuantityTypeIdentifierWaterTemperature";

/** Average-water-temp metadata keys a non-Ultra watch may stamp instead of a
 *  per-sample bucket. Both the bare ObjC constant form and a plain form. */
const WATER_TEMP_META_KEYS = [
  "HKAverageWaterTemperature",
  "HKMetadataKeyAverageWaterTemperature",
  "averageWaterTemperature",
] as const;

export interface WaterTemp {
  /** Numeric temperature in `unit`. */
  value: number;
  /** HealthKit unit string as reported by the source (e.g. "degF", "degC"). */
  unit: string;
}

/**
 * Extract the swim's water temperature, or null when no honest signal exists.
 * Bucket average wins; a metadata average is the fallback; anything else omits.
 */
export function openWaterTemp(dump: SwimFirehoseResponse): WaterTemp | null {
  const bucket = dump.buckets.find((b) => b.typeName === WATER_TEMP_TYPE) as
    | FirehoseTypeBucket
    | undefined;
  if (bucket && bucket.samples.length > 0) {
    const nums = bucket.samples
      .map((s) => s.value)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (nums.length > 0) {
      const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
      return { value: avg, unit: bucket.unit ?? "degC" };
    }
  }

  const metaRaw = readMetaValue(dump.metadata, WATER_TEMP_META_KEYS);
  if (metaRaw !== undefined && metaRaw !== null) {
    const n = typeof metaRaw === "number" ? metaRaw : parseFloat(String(metaRaw));
    if (Number.isFinite(n)) return { value: n, unit: "degC" };
  }

  return null;
}

/** Render a WaterTemp as a compact label ("73°F"), or null to omit entirely.
 *  Always Fahrenheit: the Watch records water temp in Celsius but the owner
 *  reads °F (owner call, 2026-07-11 — matches the Fitness app's display). */
export function formatWaterTemp(t: WaterTemp | null): string | null {
  if (!t) return null;
  const isF = t.unit.toLowerCase().includes("f");
  const f = isF ? t.value : t.value * 9 / 5 + 32;
  return `${Math.round(f)}°F`;
}
